import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Avatar from './Avatar';
import Button from './Button';
import Icon from './Icon';
import CameraCaptureModal from './CameraCaptureModal';
import AvatarViewerModal from './AvatarViewerModal';
import { uploadAvatarApi, deleteAvatarApi } from '../services/api';
import { useAppStore } from '../store/useAppStore';

function formatElapsed(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function useElapsed(isClockedIn: boolean, since?: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isClockedIn || !since) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isClockedIn, since]);
  return isClockedIn && since ? formatElapsed(now - new Date(since).getTime()) : '00:00:00';
}

interface AvatarUploadProps {
  firstName: string;
  lastName?: string;
  avatarUrl?: string;
  avatarColor?: string;
  size: 'sm' | 'xl';
}

const avatarMenuItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
  padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
  textAlign: 'left', fontSize: 'var(--fs-sm)', color: 'var(--ink)', whiteSpace: 'nowrap',
};

// Profile picture, uploaded through SK Drive (see sk-people-api's
// platform/drive.client.ts) — always the logged-in user's own picture, so no
// role/ownership check is needed here the way Profile.tsx's edit form needs one.
function AvatarUpload({ firstName, lastName, avatarUrl, avatarColor, size }: AvatarUploadProps) {
  const { toast } = useAppStore();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-avatar-menu]')) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const uploadMutation = useMutation({
    mutationFn: uploadAvatarApi,
    // Write the response straight into the 'me' cache slot rather than
    // invalidating it — WebShell's `fetchMyEmployee` query is `enabled:
    // !employeeId`, so it only ever runs once at login and permanently
    // disables itself afterward. invalidateQueries has nothing left to
    // refetch at that point (which is why the picture only ever showed up
    // after a full reload, when that resolution step runs again from
    // scratch); setQueryData updates the cache directly regardless of
    // whether the query is still "enabled".
    onSuccess: (updated) => {
      queryClient.setQueryData(['employees', 'me'], updated);
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast('Profile picture updated');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Failed to upload picture', 'danger'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAvatarApi,
    onSuccess: (updated) => {
      queryClient.setQueryData(['employees', 'me'], updated);
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast('Profile picture removed');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Failed to remove picture', 'danger'),
  });

  const busy = uploadMutation.isPending || deleteMutation.isPending;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadMutation.mutate(file);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }} data-avatar-menu>
      <button
        type="button"
        title="Profile picture"
        onClick={() => setMenuOpen((v) => !v)}
        disabled={busy}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', borderRadius: '50%', opacity: busy ? 0.6 : 1 }}
      >
        <Avatar firstName={firstName} lastName={lastName} src={avatarUrl} color={avatarColor} size={size} />
      </button>

      {menuOpen && (
        <div
          style={{
            position: 'absolute', top: 0, left: 'calc(100% + 8px)', zIndex: 200, minWidth: 200,
            background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.14)', overflow: 'hidden', padding: '6px 0',
          }}
        >
          {avatarUrl && (
            <>
              <button
                type="button"
                style={avatarMenuItemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                onClick={() => { setMenuOpen(false); setShowViewer(true); }}
              >
                <Icon name="eye" size={14} color="var(--ink-2)" />
                <span>View photo</span>
              </button>
              <div style={{ margin: '6px 0', borderTop: '1px solid var(--border)' }} />
            </>
          )}
          <button
            type="button"
            style={avatarMenuItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            onClick={() => { setMenuOpen(false); setShowCamera(true); }}
          >
            <Icon name="camera" size={14} color="var(--ink-2)" />
            <span>Take from camera</span>
          </button>
          <button
            type="button"
            style={avatarMenuItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            onClick={() => { setMenuOpen(false); fileInputRef.current?.click(); }}
          >
            <Icon name="upload" size={14} color="var(--ink-2)" />
            <span>Upload from computer</span>
          </button>
          {avatarUrl && (
            <>
              <div style={{ margin: '6px 0', borderTop: '1px solid var(--border)' }} />
              <button
                type="button"
                style={{ ...avatarMenuItemStyle, color: '#dc2626' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                onClick={() => { setMenuOpen(false); deleteMutation.mutate(); }}
              >
                <Icon name="trash" size={14} color="#dc2626" />
                <span>Remove</span>
              </button>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {showCamera && (
        <CameraCaptureModal
          onClose={() => setShowCamera(false)}
          onCapture={(file) => { setShowCamera(false); uploadMutation.mutate(file); }}
        />
      )}

      {showViewer && avatarUrl && (
        <AvatarViewerModal
          firstName={firstName}
          lastName={lastName}
          src={avatarUrl}
          onClose={() => setShowViewer(false)}
        />
      )}
    </div>
  );
}

interface ClockCardProps {
  firstName: string;
  lastName?: string;
  employeeCode?: string;
  avatarUrl?: string;
  avatarColor?: string;
  isClockedIn: boolean;
  since?: string; // ISO timestamp of the last clock-in, when isClockedIn is true
  disabled?: boolean;
  pending?: boolean;
  onToggle: () => void;
  variant?: 'card' | 'compact';
}

export default function ClockCard({
  firstName, lastName, employeeCode, avatarUrl, avatarColor,
  isClockedIn, since, disabled, pending, onToggle, variant = 'card',
}: ClockCardProps) {
  const elapsed = useElapsed(isClockedIn, since);

  if (variant === 'compact') {
    return (
      <div style={{ padding: '10px 12px', margin: '0 8px 10px', borderRadius: 10, background: 'var(--paper-3,#f3f4f6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AvatarUpload firstName={firstName} lastName={lastName} avatarUrl={avatarUrl} avatarColor={avatarColor} size="sm" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {firstName} {lastName}
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, color: isClockedIn ? 'var(--success)' : 'var(--ink-3)' }}>
              {isClockedIn ? 'In' : 'Out'}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--ink)', textAlign: 'center', margin: '8px 0' }}>
          {elapsed}
        </div>
        <Button
          variant={isClockedIn ? 'danger' : 'accent'}
          size="sm"
          block
          disabled={disabled || pending}
          onClick={onToggle}
        >
          {pending ? 'Please wait…' : isClockedIn ? 'Check-out' : 'Check-in'}
        </Button>
      </div>
    );
  }

  return (
    <div className="t-card" style={{ textAlign: 'center', padding: '28px 20px', maxWidth: 320 }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
        <AvatarUpload firstName={firstName} lastName={lastName} avatarUrl={avatarUrl} avatarColor={avatarColor} size="xl" />
      </div>
      <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--ink)' }}>
        {employeeCode ? `${employeeCode} - ` : ''}{firstName} {lastName}
      </div>
      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: isClockedIn ? 'var(--success)' : 'var(--ink-3)', marginTop: 6 }}>
        {isClockedIn ? 'In' : 'Out'}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '16px 0' }}>
        {elapsed.split(':').map((part, i) => (
          <div key={i} style={{
            background: 'var(--neutral-soft)', borderRadius: 10, padding: '10px 14px',
            fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--ink)', minWidth: 44,
          }}>
            {part}
          </div>
        ))}
      </div>
      <Button
        variant={isClockedIn ? 'danger' : 'accent'}
        block
        disabled={disabled || pending}
        onClick={onToggle}
      >
        {pending ? 'Please wait…' : isClockedIn ? 'Check-out' : 'Check-in'}
      </Button>
    </div>
  );
}
